import React, { useContext, useState } from "react";
import Header from "../header/header";
import Footer from "../footer/footer";
import { useNavigate } from "react-router-dom";
import { AppContext } from "../../../services/context";
import { jwtDecode } from 'jwt-decode';
import { baseApiUrl } from "../../../services/api";

function Login() {
    const authCtx = useContext(AppContext);
    const { onUserChange } = authCtx;
    const navigate = useNavigate();

    const [login, setLogin] = useState({
      username: "",
      password: "",
    });
    const [loginFail, setLoginFail] = useState(false);
    const [passwordVisible, setPasswordVisible] = useState(false);

    // États pour la réinitialisation de mot de passe
    const [currentStep, setCurrentStep] = useState("login"); // login, resetStep
    const [resetData, setResetData] = useState({
      email: "",
      newPassword: "",
      confirmPassword: ""
    });
    const [resetStatus, setResetStatus] = useState({
      message: "",
      isError: false,
      isSuccess: false
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [resetPasswordVisible, setResetPasswordVisible] = useState(false);

    // Gérer l'affichage du mot de passe
    const togglePasswordVisibility = () => {
      setPasswordVisible(!passwordVisible);
    };

    const toggleResetPasswordVisibility = () => {
      setResetPasswordVisible(!resetPasswordVisible);
    };

    // Fonction pour gérer le changement dans les champs de texte
    const onChange = (e) => {
      e.preventDefault();
      setLogin({
        ...login,
        [e.target.name]: e.target.value,
      });
    };

    const handleResetDataChange = (e) => {
      setResetData({
        ...resetData,
        [e.target.name]: e.target.value
      });
    };

    const handleSubmit = (e) => {
      e.preventDefault();
      setLoginFail(false);

      baseApiUrl.post("compte/connexion", login)
        .then((res) => {
          if(res.data && res.data.bearer && res.data.refresh) {
            localStorage.setItem("token", res.data.bearer);
            localStorage.setItem("refreshToken", res.data.refresh);

            const decodedToken = jwtDecode(res.data.bearer);

            const user = {
              isAuth: true,
              id: decodedToken.id || "",
              nom: decodedToken.nom || "",
              prenom: decodedToken.prenom || "",
              photo: decodedToken.photo || "",
              role: decodedToken.roles ? decodedToken.roles[0] : "",
              exp: decodedToken.exp|| "",
              token: res.data.bearer,
              refreshToken: res.data.refresh,
            };

            localStorage.setItem("user", JSON.stringify(user));
            onUserChange(user);

            // Rediriger en fonction du rôle
            if (user.role === "ROLE_ADMINISTRATEUR" || user.role === "ROLE_MANAGER") {
              navigate('/admin');
            } else {
              navigate('/');
            }
          } else {
            setLoginFail(true);
          }
        })
        .catch((error) => {
          setLoginFail(true);
          console.error("Erreur de connexion:", error);
        });
    };

    // Réinitialisation directe du mot de passe sans code
    const handleResetPassword = (e) => {
      e.preventDefault();

      // Vérifications des champs
      if (!resetData.email.trim()) {
        setResetStatus({
          message: "Veuillez entrer votre adresse email.",
          isError: true,
          isSuccess: false
        });
        return;
      }

      if (!resetData.newPassword) {
        setResetStatus({
          message: "Veuillez entrer un nouveau mot de passe.",
          isError: true,
          isSuccess: false
        });
        return;
      }

      if (resetData.newPassword !== resetData.confirmPassword) {
        setResetStatus({
          message: "Les mots de passe ne correspondent pas.",
          isError: true,
          isSuccess: false
        });
        return;
      }

      setIsSubmitting(true);
      setResetStatus({ message: "", isError: false, isSuccess: false });

      // Essayons d'utiliser l'API existante mais en omettant le code
      // Si cela ne fonctionne pas, nous pourrons essayer d'autres endpoints
      baseApiUrl.post("compte/modifier-mot-de-passe", {
        email: resetData.email,
        // Omission volontaire du code pour forcer une mise à jour directe
        // Si le backend exige un code, nous pouvons envoyer une valeur par défaut
        code: "DIRECT_RESET", // Code spécial pour indiquer une réinitialisation directe
        nouveauMotDePasse: resetData.newPassword
      })
        .then((response) => {
          setResetStatus({
            message: "Votre mot de passe a été réinitialisé avec succès. Vous pouvez maintenant vous connecter avec votre nouveau mot de passe.",
            isError: false,
            isSuccess: true
          });

          // Après réinitialisation réussie, suggérer une connexion immédiate
          setLogin({
            username: resetData.email,
            password: ""  // Le champ du mot de passe reste vide pour des raisons de sécurité
          });

          // Rediriger vers la page de connexion après 3 secondes
          setTimeout(() => {
            setCurrentStep("login");
            setResetData({
              email: "",
              newPassword: "",
              confirmPassword: ""
            });
            setResetStatus({ message: "", isError: false, isSuccess: false });
          }, 3000);
        })
        .catch((error) => {
          console.error("Erreur de réinitialisation:", error);

          // Si la première tentative échoue, essayons une autre approche
          // C'est ici qu'on pourrait essayer un autre endpoint si disponible
          let errorMessage = "Une erreur s'est produite lors de la réinitialisation du mot de passe. Veuillez réessayer.";

          if (error.response && error.response.data && error.response.data.message) {
            errorMessage = error.response.data.message;
          }

          setResetStatus({
            message: errorMessage,
            isError: true,
            isSuccess: false
          });
        })
        .finally(() => {
          setIsSubmitting(false);
        });
    };

    // Retour à la page de connexion
    const handleBackToLogin = () => {
      setCurrentStep("login");
      setResetData({
        email: "",
        newPassword: "",
        confirmPassword: ""
      });
      setResetStatus({ message: "", isError: false, isSuccess: false });
    };

    // Navigation au formulaire de réinitialisation
    const navigateToPasswordReset = () => {
      setCurrentStep("resetStep");
      setResetStatus({ message: "", isError: false, isSuccess: false });
    };

    // Rendu des titres de page en fonction de l'étape courante
    const getPageTitle = () => {
      switch(currentStep) {
        case "resetStep":
          return "Réinitialisation de mot de passe";
        default:
          return "Connectez-vous";
      }
    };

    return (
      <div>
        <Header />
        <div className="login-page sp bg-cover">
          <div className="container">
            <div className="row">
              <div className="col-lg-12">
                <div className="inner-main-heading">
                  <div className="page-prog"></div>
                  <h1>{getPageTitle()}</h1>
                </div>
              </div>
            </div>
            <div className="row">
              <div className="col-lg-5 m-auto">
                {currentStep === "login" && (
                  // Formulaire de connexion
                  <div className="login-form">
                    <h3>Bienvenue</h3>
                    <p>Entrez votre email et mot de passe pour vous connecter.</p>
                    <form onSubmit={handleSubmit}>
                      <div className="form-floating mb-3">
                        <input
                          type="email"
                          className="form-control"
                          id="floatingInput"
                          placeholder="name@example.com"
                          name="username"
                          value={login.username}
                          onChange={onChange}
                          required
                        />
                        <label htmlFor="floatingInput">Email</label>
                      </div>
                      <div className="form-floating mb-3 position-relative">
                        <input
                          type={passwordVisible ? "text" : "password"}
                          className="form-control"
                          id="floatingPassword"
                          placeholder="Password"
                          name="password"
                          value={login.password}
                          onChange={onChange}
                          required
                        />
                        <i
                          className={passwordVisible ? "fas fa-eye-slash position-absolute end-0 top-50 translate-middle-y px-2" : "fas fa-eye position-absolute end-0 top-50 translate-middle-y px-2"}
                          onClick={togglePasswordVisibility}
                          style={{ cursor: "pointer", zIndex: 5 }}
                        ></i>
                        <label htmlFor="floatingPassword">Mot de passe</label>
                      </div>

                      {loginFail && (
                        <div className="alert alert-danger text-center mb-3">
                          Email ou mot de passe incorrect. Veuillez réessayer.
                        </div>
                      )}

                      {/* <div className="d-flex justify-content-end mb-3">
                        <button
                          type="button"
                          className="btn btn-link p-0 text-decoration-none"
                          onClick={navigateToPasswordReset}
                          style={{ color: "#0075FF", fontSize: "14px", fontWeight: "500" }}
                        >
                          Mot de passe oublié?
                        </button>
                      </div> */}

                      <div className="button mt-20">
                        <button type="submit" className="theme-btn1">
                          Se connecter
                        </button>
                      </div>

                      <div className="text-center mt-4">
                        <p className="text">
                          Vous n'avez pas de compte?{" "}
                          <a href="/contact">contactez-nous</a>
                        </p>
                      </div> <div className="text-center mt-4">
                        <p className="text">
                          Creer votre compte?{" "}
                          <a href="/inscription">inscription</a>
                        </p>
                      </div>
                    </form>
                  </div>
                )}

                {currentStep === "resetStep" && (
                  // Formulaire de réinitialisation directe du mot de passe
                  <div className="login-form">
                    <h3>Réinitialisation de mot de passe</h3>
                    <p>Entrez votre email et votre nouveau mot de passe.</p>

                    {resetStatus.message && (
                      <div className={`alert ${resetStatus.isError ? 'alert-danger' : resetStatus.isSuccess ? 'alert-success' : ''} text-center mb-3`}>
                        {resetStatus.message}
                      </div>
                    )}

                    <form onSubmit={handleResetPassword}>
                      <div className="form-floating mb-3">
                        <input
                          type="email"
                          className="form-control"
                          id="resetEmail"
                          placeholder="name@example.com"
                          name="email"
                          value={resetData.email}
                          onChange={handleResetDataChange}
                          required
                        />
                        <label htmlFor="resetEmail">Email</label>
                      </div>

                      <div className="form-floating mb-3 position-relative">
                        <input
                          type={resetPasswordVisible ? "text" : "password"}
                          className="form-control"
                          id="newPassword"
                          placeholder="Nouveau mot de passe"
                          name="newPassword"
                          value={resetData.newPassword}
                          onChange={handleResetDataChange}
                          required
                        />
                        <i
                          className={resetPasswordVisible ? "fas fa-eye-slash position-absolute end-0 top-50 translate-middle-y px-2" : "fas fa-eye position-absolute end-0 top-50 translate-middle-y px-2"}
                          onClick={toggleResetPasswordVisibility}
                          style={{ cursor: "pointer", zIndex: 5 }}
                        ></i>
                        <label htmlFor="newPassword">Nouveau mot de passe</label>
                      </div>

                      <div className="form-floating mb-3">
                        <input
                          type={resetPasswordVisible ? "text" : "password"}
                          className="form-control"
                          id="confirmPassword"
                          placeholder="Confirmer le mot de passe"
                          name="confirmPassword"
                          value={resetData.confirmPassword}
                          onChange={handleResetDataChange}
                          required
                        />
                        <label htmlFor="confirmPassword">Confirmer le mot de passe</label>
                      </div>

                      <div className="d-flex justify-content-between align-items-center mt-4">
                        <button
                          type="button"
                          className="btn btn-outline-secondary"
                          onClick={handleBackToLogin}
                          disabled={isSubmitting}
                        >
                          Retour à la connexion
                        </button>

                        <button
                          type="submit"
                          className="theme-btn1"
                          disabled={isSubmitting || resetStatus.isSuccess}
                        >
                          {isSubmitting ? (
                            <>
                              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                              Traitement...
                            </>
                          ) : "Réinitialiser le mot de passe"}
                        </button>
                      </div>
                    </form>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
}

export default Login;
